/**
 * Data Share — turn each provider's own conversation format into `ShareConversation`.
 *
 * Pure functions, no I/O: the server uses them on fetched share links, the browser on export files.
 *
 * Formats (as observed 2026-11-04):
 *   - Claude: share snapshot JSON and the account export's conversations.json both carry
 *     `chat_messages[]` with `sender` and typed `content[]` (text, thinking, tool_use, tool_result).
 *   - ChatGPT: share pages embed the conversation as a React Router "turbo-stream" (decoded here);
 *     it has `linear_conversation[]`. The account export's conversations.json has the same message
 *     objects in a `mapping` tree, walked from `current_node` back to the root.
 *   - Gemini: the share RPC returns nested arrays; each turn holds the prompt at [2][0][0] and the
 *     reply at [3][0][0][1][0].
 *
 * Everything we cannot read is skipped rather than guessed at: custom instructions and saved
 * memories inside ChatGPT messages (user_editable_context / model_editable_context) are personal
 * settings, not conversation, so they are dropped.
 */

import type { ShareAttachment, ShareConversation, ShareMessage, ShareProvider, ShareSource } from "./types";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

const TOOL_TEXT_CAP = 8_000;

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function clip(s: string, n = TOOL_TEXT_CAP): string {
  return s.length > n ? `${s.slice(0, n)}\n… [${(s.length - n).toLocaleString()} more characters]` : s;
}

function iso(v: unknown): string | undefined {
  if (typeof v === "string" && v) return v;
  if (typeof v === "number" && Number.isFinite(v) && v > 0) {
    return new Date(v < 1e12 ? v * 1000 : v).toISOString();
  }
  return undefined;
}

/* ───────────────────────────── Claude ───────────────────────────── */

function claudeBlockText(c: Any): string {
  if (typeof c === "string") return c;
  if (Array.isArray(c)) return c.map(claudeBlockText).filter(Boolean).join("\n");
  if (c && typeof c === "object") return str(c.text) || str(c.content) || "";
  return "";
}

function num(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function claudeAttachments(m: Any): ShareAttachment[] {
  const out: ShareAttachment[] = [];
  for (const a of Array.isArray(m?.attachments) ? m.attachments : []) {
    out.push({ kind: "file", name: str(a?.file_name) || "attachment", mime: str(a?.file_type) || undefined, bytes: num(a?.file_size) });
  }
  const files: Any[] = Array.isArray(m?.files) ? m.files : Array.isArray(m?.files_v2) ? m.files_v2 : [];
  for (const f of files) {
    const kind = str(f?.file_kind) === "image" ? "image" : "file";
    out.push({
      kind,
      name: str(f?.file_name) || (kind === "image" ? "image" : "file"),
      width: num(f?.preview_asset?.image_width ?? f?.thumbnail_asset?.image_width),
      height: num(f?.preview_asset?.image_height ?? f?.thumbnail_asset?.image_height),
    });
  }
  return out;
}

export function claudeMessages(chat: Any[]): ShareMessage[] {
  const out: ShareMessage[] = [];
  for (const m of Array.isArray(chat) ? chat : []) {
    const role = m?.sender === "human" ? "user" : "assistant";
    const at = iso(m?.created_at);
    const attachments = claudeAttachments(m);
    const voice = str(m?.input_mode).toLowerCase().includes("voice") || undefined;
    let attached = false;
    const take = () => {
      if (attached || !attachments.length) return undefined;
      attached = true;
      return attachments;
    };

    for (const a of Array.isArray(m?.attachments) ? m.attachments : []) {
      const t = str(a?.extracted_content);
      if (t) out.push({ role: "user", kind: "document", name: str(a?.file_name) || "attachment", text: clip(t, 40_000), at });
    }

    const content: Any[] = Array.isArray(m?.content) ? m.content : [];
    if (!content.length) {
      const t = str(m?.text);
      if (t || attachments.length) out.push({ role, kind: "text", text: t, at, voice, attachments: take() });
      continue;
    }
    for (const c of content) {
      switch (c?.type) {
        case "text":
          if (str(c.text)) {
            out.push({
              role,
              kind: "text",
              text: c.text,
              at,
              voice: role === "user" ? voice : undefined,
              attachments: role === "user" ? take() : undefined,
            });
          }
          break;
        case "thinking":
          if (str(c.thinking)) out.push({ role: "assistant", kind: "thinking", text: c.thinking, at });
          break;
        case "tool_use": {
          let input = "";
          try {
            input = typeof c.input === "string" ? c.input : JSON.stringify(c.input ?? {}, null, 2);
          } catch {
            input = "";
          }
          out.push({ role: "assistant", kind: "tool_call", name: str(c.name) || "tool", text: clip(input), at });
          break;
        }
        case "tool_result": {
          const t = claudeBlockText(c.content);
          out.push({ role: "tool", kind: "tool_result", name: str(c.name) || "tool", text: clip(t), at, isError: c.is_error === true || undefined });
          break;
        }
        default:
          break;
      }
    }
    // Images with no text alongside them still count.
    if (!attached && attachments.length) out.push({ role, kind: "text", text: "", at, attachments });
  }
  return out;
}

export function claudeConversation(d: Any, source: ShareSource, id?: string, url?: string): ShareConversation {
  const meta: Record<string, string> = {};
  if (str(d?.project?.name)) meta.project = d.project.name;
  if (str(d?.project_uuid)) meta.project_id = d.project_uuid;
  if (str(d?.summary)) meta.summary = d.summary;
  return {
    provider: "claude",
    source,
    id: id || str(d?.uuid) || str(d?.conversation_uuid) || "claude",
    title: str(d?.snapshot_name) || str(d?.name) || "Untitled Claude chat",
    url,
    createdAt: iso(d?.created_at),
    updatedAt: iso(d?.updated_at),
    model: str(d?.model) || undefined,
    meta: Object.keys(meta).length ? meta : undefined,
    messages: claudeMessages(d?.chat_messages),
  };
}

/* ───────────────────────────── ChatGPT ───────────────────────────── */

const SKIP_TYPES = new Set([
  "user_editable_context",
  "model_editable_context",
  "reasoning_recap",
  "tether_browsing_display",
  "system_error",
]);

function chatgptMessage(m: Any): ShareMessage[] {
  if (!m || typeof m !== "object") return [];
  const meta = m.metadata || {};
  if (meta.is_visually_hidden_from_conversation) return [];
  const content = m.content || {};
  const type = str(content.content_type);
  if (SKIP_TYPES.has(type)) return [];

  const authorRole = str(m.author?.role);
  const name = str(m.author?.name) || undefined;
  const at = iso(m.create_time);
  const model = str(meta.model_slug) || str(meta.default_model_slug) || undefined;
  const role = authorRole === "user" ? "user" : authorRole === "tool" ? "tool" : authorRole === "system" ? "system" : "assistant";

  if (type === "thoughts") {
    const t = (Array.isArray(content.thoughts) ? content.thoughts : [])
      .map((x: Any) => [str(x?.summary), str(x?.content)].filter(Boolean).join("\n"))
      .filter(Boolean)
      .join("\n\n");
    return t ? [{ role: "assistant", kind: "thinking", text: t, at, model }] : [];
  }
  if (type === "code") {
    const t = str(content.text);
    return t ? [{ role: "assistant", kind: "tool_call", name: str(m.recipient) || name || "code", text: clip(t), at, model }] : [];
  }
  if (type === "execution_output") {
    const t = str(content.text);
    return t ? [{ role: "tool", kind: "tool_result", name: name || "python", text: clip(t), at }] : [];
  }
  if (type === "tether_quote") {
    const t = str(content.text);
    return t ? [{ role: "tool", kind: "document", name: str(content.title) || str(content.domain) || "quote", text: clip(t), at }] : [];
  }

  const attachments: ShareAttachment[] = [];
  let voice = false;
  const parts: Any[] = Array.isArray(content.parts) ? content.parts : [];
  const text = parts
    .map((p) => {
      if (typeof p === "string") return p;
      if (p && typeof p === "object") {
        const ct = str(p.content_type);
        if (ct === "image_asset_pointer") {
          attachments.push({ kind: "image", name: "image", bytes: num(p.size_bytes), width: num(p.width), height: num(p.height) });
          return "";
        }
        if (ct === "audio_transcription") {
          voice = true;
          return str(p.text);
        }
        if (ct.includes("audio") || ct.includes("video")) {
          attachments.push({ kind: "audio", name: "voice recording", bytes: num(p.size_bytes) });
          return "";
        }
        return str(p.text);
      }
      return "";
    })
    .filter(Boolean)
    .join("\n")
    .trim();
  for (const a of Array.isArray(meta.attachments) ? meta.attachments : []) {
    const mime = str(a?.mime_type || a?.mimeType);
    const unnamed = attachments.findIndex((x) => x.kind === "image" && x.name === "image");
    if (mime.startsWith("image/") && unnamed >= 0) {
      attachments[unnamed] = { ...attachments[unnamed], name: str(a?.name) || "image", mime };
      continue;
    }
    attachments.push({
      kind: mime.startsWith("image/") ? "image" : "file",
      name: str(a?.name) || "file",
      mime: mime || undefined,
      bytes: num(a?.size),
    });
  }
  if (!text && !attachments.length) return [];
  if (role === "tool") {
    if (/^The output of this plugin was redacted\.?$/.test(text)) return [];
    return [{ role: "tool", kind: "tool_result", name, text: clip(text), at, attachments: attachments.length ? attachments : undefined }];
  }
  if (role === "system") return []; // system prompts are the provider's, not the user's
  return [
    {
      role,
      kind: "text",
      text,
      at,
      model: role === "assistant" ? model : undefined,
      voice: voice || undefined,
      attachments: attachments.length ? attachments : undefined,
    },
  ];
}

/** From a share page (`linear_conversation`) or an export record (`mapping` + `current_node`). */
export function chatgptConversation(c: Any, source: ShareSource, id?: string, url?: string): ShareConversation {
  let nodes: Any[] = [];
  if (Array.isArray(c?.linear_conversation) && c.linear_conversation.length) {
    nodes = c.linear_conversation;
  } else if (c?.mapping && typeof c.mapping === "object") {
    // Walk the branch the user actually ended on, from the leaf to the root, then reverse.
    const chain: Any[] = [];
    const seen = new Set<string>();
    let cur: string | undefined = str(c.current_node) || undefined;
    while (cur && c.mapping[cur] && !seen.has(cur)) {
      seen.add(cur);
      chain.push(c.mapping[cur]);
      cur = c.mapping[cur].parent || undefined;
    }
    nodes = chain.reverse();
  }
  const messages = nodes.flatMap((n) => chatgptMessage(n?.message));
  const meta: Record<string, string> = {};
  if (str(c?.gizmo_id)) meta.custom_gpt = c.gizmo_id;
  if (str(c?.conversation_template_id)) meta.project_id = c.conversation_template_id;
  if (c?.is_study_mode === true) meta.mode = "study";
  return {
    provider: "chatgpt",
    source,
    id: id || str(c?.conversation_id) || str(c?.id) || "chatgpt",
    title: str(c?.title) || "Untitled ChatGPT chat",
    url,
    createdAt: iso(c?.create_time),
    updatedAt: iso(c?.update_time),
    model: str(c?.default_model_slug) || messages.find((m) => m.model)?.model,
    meta: Object.keys(meta).length ? meta : undefined,
    messages,
  };
}

/**
 * Decode a React Router turbo-stream payload (the array ChatGPT's share page passes to
 * `streamController.enqueue`). Objects are written as {"_<keyIndex>": valueIndex}; arrays as lists
 * of value indexes; negative numbers are constants. Shared values are decoded once (memoised),
 * which also makes cycles safe.
 */
export function decodeTurboStream(arr: Any[]): Any {
  const memo = new Map<number, Any>();
  const CONST: Record<number, Any> = { [-1]: undefined, [-2]: undefined, [-3]: NaN, [-4]: Infinity, [-5]: null, [-6]: -Infinity, [-7]: -0 };
  const dec = (idx: number): Any => {
    if (idx < 0) return CONST[idx];
    if (memo.has(idx)) return memo.get(idx);
    const v = arr[idx];
    if (Array.isArray(v)) {
      if (typeof v[0] === "string") {
        memo.set(idx, v); // a typed special (date, promise, ...): not needed here
        return v;
      }
      const out: Any[] = [];
      memo.set(idx, out);
      for (const x of v) out.push(typeof x === "number" ? dec(x) : x);
      return out;
    }
    if (v && typeof v === "object") {
      const out: Record<string, Any> = {};
      memo.set(idx, out);
      for (const [k, x] of Object.entries(v)) {
        const key = k.startsWith("_") ? String(arr[Number(k.slice(1))]) : k;
        out[key] = typeof x === "number" ? dec(x) : x;
      }
      return out;
    }
    memo.set(idx, v);
    return v;
  };
  return dec(0);
}

/** Find the first object that looks like a ChatGPT conversation. Breadth-first, cycle-safe. */
export function findChatgptConversation(root: Any): Any | null {
  const queue: Any[] = [root];
  const seen = new Set<Any>();
  let steps = 0;
  while (queue.length && steps++ < 200_000) {
    const o = queue.shift();
    if (!o || typeof o !== "object" || seen.has(o)) continue;
    seen.add(o);
    if (!Array.isArray(o) && ("linear_conversation" in o || "mapping" in o) && "title" in o) return o;
    for (const v of Array.isArray(o) ? o : Object.values(o)) if (v && typeof v === "object") queue.push(v);
  }
  return null;
}

/* ───────────────────────────── Gemini ───────────────────────────── */

function cleanGemini(t: string): string {
  // Gemini tags code fences with internal references (```python?code_reference&code_event_index=1).
  return t.replace(/```([a-zA-Z0-9_+-]*)\?[^\n]*/g, "```$1").trim();
}

function pick(v: Any, ...path: number[]): Any {
  let cur = v;
  for (const p of path) {
    if (!Array.isArray(cur)) return undefined;
    cur = cur[p];
  }
  return cur;
}

export function geminiConversation(core: Any, id: string, url?: string): ShareConversation {
  const turns: Any[] = Array.isArray(pick(core, 1)) ? pick(core, 1) : [];
  const messages: ShareMessage[] = [];
  for (const t of turns) {
    const prompt = str(pick(t, 2, 0, 0));
    if (prompt) messages.push({ role: "user", kind: "text", text: prompt });
    const reply = str(pick(t, 3, 0, 0, 1, 0));
    if (reply) messages.push({ role: "assistant", kind: "text", text: cleanGemini(reply) });
  }
  const created = pick(core, 4);
  return {
    provider: "gemini",
    source: "link",
    id,
    title: str(pick(core, 2, 1)) || "Untitled Gemini chat",
    url,
    createdAt: Array.isArray(created) && typeof created[0] === "number" ? iso(created[0]) : undefined,
    messages,
  };
}

/* ─────────────────────── Memory / project documents ─────────────────────── */

/** One markdown file from a Claude memory export ("---\nname: ...\ndescription: ...\n---\nbody"). */
export function memoryDoc(path: string, text: string): ShareMessage {
  let body = text;
  let name = path.split("/").pop() || path;
  const fm = /^---\s*\n([\s\S]*?)\n---\s*\n?/.exec(text);
  if (fm) {
    body = text.slice(fm[0].length);
    const n = /^name:\s*"?([^"\n]+)"?/m.exec(fm[1]);
    const d = /^description:\s*"?([^"\n]+)"?/m.exec(fm[1]);
    if (n) name = n[1];
    if (d) body = `${d[1]}\n\n${body}`;
  }
  return { role: "user", kind: "memory", name, text: body.trim() };
}

export function documentsConversation(
  provider: ShareProvider,
  source: ShareSource,
  id: string,
  title: string,
  docs: ShareMessage[],
): ShareConversation {
  return { provider, source, id, title, messages: docs };
}

/* ───────────────────────────── DeepSeek ───────────────────────────── */

/**
 * DeepSeek splits each message into typed fragments: REQUEST (the user's text), THINK (reasoning),
 * RESPONSE (the answer), SEARCH (web results it read), and others for tools. Both the share API and
 * the account export use them; the share API adds a role, the export does not (it is implied by the
 * fragment types).
 */
function deepseekFragments(frags: Any[], at: string | undefined, model: string | undefined, roleHint?: string): ShareMessage[] {
  const out: ShareMessage[] = [];
  for (const f of Array.isArray(frags) ? frags : []) {
    const type = str(f?.type).toUpperCase();
    const content = str(f?.content);
    if (type === "REQUEST") {
      if (content) out.push({ role: "user", kind: "text", text: content, at });
    } else if (type === "THINK") {
      if (content) out.push({ role: "assistant", kind: "thinking", text: content, at, model });
    } else if (type === "RESPONSE") {
      if (content) out.push({ role: "assistant", kind: "text", text: content, at, model });
    } else if (type === "SEARCH") {
      const results: Any[] = Array.isArray(f?.results) ? f.results : [];
      const text = results.map((r) => [str(r?.title), str(r?.url)].filter(Boolean).join(" ")).filter(Boolean).join("\n");
      out.push({ role: "tool", kind: "tool_result", name: "web search", text: clip(text || content), at });
    } else if (content) {
      // Unknown fragment kinds (tool calls, files): keep the text, labelled by its type.
      out.push({ role: roleHint === "USER" ? "user" : "tool", kind: roleHint === "USER" ? "text" : "tool_result", name: type.toLowerCase(), text: clip(content), at });
    }
  }
  return out;
}

/** GET chat.deepseek.com/api/v0/share/content?share_id=<id>  ->  data.biz_data */
export function deepseekShareConversation(biz: Any, id: string, url?: string): ShareConversation {
  const msgs: Any[] = Array.isArray(biz?.messages) ? biz.messages : [];
  const messages: ShareMessage[] = [];
  let maxTokens = 0;
  for (const m of msgs) {
    const model = str(m?.model) || undefined;
    messages.push(...deepseekFragments(m?.fragments, iso(m?.inserted_at), model, str(m?.role).toUpperCase()));
    // accumulated_token_usage is a running total for the conversation, so the last value is the total.
    const t = num(m?.accumulated_token_usage);
    if (t && t > maxTokens) maxTokens = t;
  }
  const title = str(biz?.title);
  return {
    provider: "deepseek",
    source: "link",
    id,
    // The share API titles every chat "Shared Conversation"; the first question says more.
    title:
      title && title !== "Shared Conversation"
        ? title
        : messages.find((m) => m.role === "user")?.text.trim().split(/\r?\n/)[0].slice(0, 80) || "DeepSeek chat",
    url,
    createdAt: messages[0]?.at,
    model: messages.find((m) => m.model)?.model || (str(biz?.model_type) && str(biz?.model_type) !== "default" ? biz.model_type : undefined),
    usage: maxTokens ? { totalTokens: maxTokens } : undefined,
    messages,
  };
}

/** One record of a DeepSeek export's conversations.json: {id, title, inserted_at, updated_at, mapping}. */
export function deepseekExportConversation(c: Any): ShareConversation {
  // The tree starts at "root"; follow the LAST child at each step (the most recent branch).
  const map = c?.mapping || {};
  const chain: Any[] = [];
  const seen = new Set<string>();
  let cur: string | undefined = map.root ? "root" : Object.keys(map).find((k) => !map[k]?.parent);
  while (cur && map[cur] && !seen.has(cur)) {
    seen.add(cur);
    chain.push(map[cur]);
    const kids: string[] = Array.isArray(map[cur].children) ? map[cur].children : [];
    cur = kids.length ? kids[kids.length - 1] : undefined;
  }
  const messages = chain.flatMap((n) =>
    n?.message ? deepseekFragments(n.message.fragments, iso(n.message.inserted_at), str(n.message.model) || undefined) : [],
  );
  return {
    provider: "deepseek",
    source: "export",
    id: str(c?.id) || "deepseek",
    title: str(c?.title) || "Untitled DeepSeek chat",
    createdAt: iso(c?.inserted_at),
    updatedAt: iso(c?.updated_at),
    model: messages.find((m) => m.model)?.model,
    messages,
  };
}

export function isDeepseekExportRecord(c: Any): boolean {
  if (!c || typeof c !== "object" || !c.mapping || typeof c.mapping !== "object") return false;
  for (const n of Object.values(c.mapping) as Any[]) {
    if (n?.message) return Array.isArray(n.message.fragments);
  }
  return false;
}

/* ───────────────────────────── Kimi ───────────────────────────── */

/** POST kimi.ai/apiv2/kimi.gateway.chat.v1.ChatService/GetChatShare {shareId}  ->  share */
export function kimiConversation(share: Any, id: string, url?: string): ShareConversation {
  const msgs: Any[] = Array.isArray(share?.messages) ? share.messages : [];
  const messages: ShareMessage[] = [];
  for (const m of msgs) {
    const role = str(m?.role) === "user" ? "user" : "assistant";
    const at = iso(m?.createTime);
    const attachments: ShareAttachment[] = [];
    for (const b of Array.isArray(m?.blocks) ? m.blocks : []) {
      if (b?.text && str(b.text.content)) {
        messages.push({ role, kind: "text", text: b.text.content, at });
      } else if (b?.think && str(b.think.content)) {
        messages.push({ role: "assistant", kind: "thinking", text: b.think.content, at });
      } else if (b?.file?.meta) {
        const meta = b.file.meta;
        const mime = str(meta.contentType);
        attachments.push({
          kind: mime.startsWith("image/") || str(meta.type).includes("IMAGE") ? "image" : "file",
          name: str(meta.name) || "file",
          mime: mime || undefined,
          bytes: Number(meta.sizeBytes) || undefined,
        });
      } else if (b?.tool || b?.toolCall) {
        const t = b.tool || b.toolCall;
        let text = "";
        try {
          text = JSON.stringify(t, null, 2);
        } catch {
          text = "";
        }
        messages.push({ role: "assistant", kind: "tool_call", name: str(t?.name) || "tool", text: clip(text), at });
      }
    }
    if (attachments.length) {
      const last = [...messages].reverse().find((x) => x.role === role && x.kind === "text" && x.at === at);
      if (last) last.attachments = attachments;
      else messages.push({ role, kind: "text", text: "", at, attachments });
    }
  }
  return {
    provider: "kimi",
    source: "link",
    id,
    title: str(share?.chat?.name) || "Kimi chat",
    url,
    createdAt: messages[0]?.at,
    messages,
  };
}

/* ───────────────────────────── Mistral (Le Chat / Vibe) ───────────────────────────── */

/**
 * A shared Mistral chat (chat.mistral.ai/work/<uuid>) is rendered on the server: the messages
 * travel inside the page as Next.js "flight" data, pushed as `self.__next_f.push([1,"..."])`.
 * In that data each message is a JSON object {"role":"user"|"assistant","content",...,"contentChunks",
 * "turn","createdAt",...}. Long strings are moved out into text rows (`<id>:T<hex byte length>,<text>`)
 * and referenced as "$<id>", so both are read here. (Observed 2026-11-05.)
 */
export function mistralFlightPayload(html: string): string {
  const re = /self\.__next_f\.push\(\[\d+,("(?:[^"\\]|\\.)*")\]\)/g;
  let out = "";
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    try {
      out += JSON.parse(m[1]);
    } catch {
      /* a malformed chunk is skipped */
    }
  }
  return out;
}

/**
 * Text rows: "<id>:T<hex length in UTF-8 bytes>,<text>". Rows are read in order from the start: an
 * ordinary row ends at a newline, but a text row ends exactly after its byte length, and the next row
 * follows with NO newline in between, so a "start of line" search misses most of them.
 */
function flightTextRows(payload: string): Map<string, string> {
  const rows = new Map<string, string>();
  const head = /([0-9a-f]+):/y;
  let p = 0;
  while (p < payload.length) {
    head.lastIndex = p;
    const m = head.exec(payload);
    if (!m) {
      const nl = payload.indexOf("\n", p);
      if (nl < 0) break;
      p = nl + 1;
      continue;
    }
    let q = head.lastIndex;
    if (payload[q] === "T") {
      const comma = payload.indexOf(",", q);
      const bytes = parseInt(payload.slice(q + 1, comma), 16);
      if (comma < 0 || !Number.isFinite(bytes)) break;
      let i = comma + 1;
      let used = 0;
      while (i < payload.length && used < bytes) {
        const c = payload.codePointAt(i)!;
        used += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
        i += c > 0xffff ? 2 : 1;
      }
      rows.set(m[1], payload.slice(comma + 1, i));
      p = i;
    } else {
      const nl = payload.indexOf("\n", q);
      if (nl < 0) break;
      p = nl + 1;
    }
    q = p;
  }
  return rows;
}

/** Cut one JSON object out of a string starting at `start`, respecting strings. */
function cutObject(s: string, start: number): string | null {
  let depth = 0;
  let inStr = false;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (inStr) {
      if (ch === "\\") i++;
      else if (ch === '"') inStr = false;
    } else if (ch === '"') inStr = true;
    else if (ch === "{") depth++;
    else if (ch === "}" && --depth === 0) return s.slice(start, i + 1);
  }
  return null;
}

export function mistralConversation(html: string, id: string, url?: string): ShareConversation | null {
  const payload = mistralFlightPayload(html);
  if (!payload) return null;
  const rows = flightTextRows(payload);
  const deref = (v: unknown): string => {
    const s = str(v);
    const m = /^\$([0-9a-f]+)$/.exec(s);
    return m ? rows.get(m[1]) ?? "" : s;
  };

  const byId = new Map<string, Any>();
  const re = /\{"role":"(?:user|assistant)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(payload))) {
    const raw = cutObject(payload, m.index);
    if (!raw) continue;
    try {
      const o = JSON.parse(raw);
      if (str(o?.chatId) && str(o.chatId) !== id) continue; // another chat's message (sidebar, etc.)
      byId.set(str(o?.id) || `${byId.size}`, o);
    } catch {
      /* not a message object */
    }
  }
  if (!byId.size) return null;

  const ordered = [...byId.values()].sort(
    (a, b) => (num(a?.turn) ?? 0) - (num(b?.turn) ?? 0) || (a?.role === "user" ? -1 : 1) - (b?.role === "user" ? -1 : 1),
  );
  const messages: ShareMessage[] = [];
  for (const o of ordered) {
    const role = o.role === "user" ? "user" : "assistant";
    const at = iso(str(o.createdAt).replace(/^\$D/, ""));
    const model = str(o.model) || undefined;
    const attachments: ShareAttachment[] = (Array.isArray(o.files) ? o.files : []).map((f: Any) => ({
      kind: str(f?.type).startsWith("image") || str(f?.mimeType).startsWith("image/") ? "image" : "file",
      name: str(f?.name) || str(f?.fileName) || "file",
      mime: str(f?.mimeType) || undefined,
    }));
    const chunks: Any[] = Array.isArray(o.contentChunks) ? o.contentChunks : [];
    if (!chunks.length) {
      const text = deref(o.content);
      if (text || attachments.length) messages.push({ role, kind: "text", text, at, model, attachments: attachments.length ? attachments : undefined });
      continue;
    }
    let first = true;
    for (const c of chunks) {
      if (c?.type === "text") {
        const text = deref(c.text);
        if (!text) continue;
        const thinking = c?._context?.type === "reasoning";
        messages.push({
          role: thinking ? "assistant" : role,
          kind: thinking ? "thinking" : "text",
          text,
          at,
          model,
          attachments: first && attachments.length ? attachments : undefined,
        });
        first = false;
      } else if (c?.type === "tool_call") {
        const name = str(c.name) || str(c?.integration?.name) || "tool";
        messages.push({ role: "assistant", kind: "tool_call", name, text: clip(deref(c.publicArguments)), at, model });
        const result = deref(c.publicResult);
        if (result) messages.push({ role: "tool", kind: "tool_result", name, text: clip(result), at, isError: c.success === false || undefined });
      }
    }
    // The chunks carry the working (reasoning, tool calls); the answer itself is `content`,
    // usually moved out to a text row and referenced as "$<id>".
    const answer = deref(o.content);
    if (answer && !messages.some((x) => x.text === answer)) {
      messages.push({ role, kind: "text", text: answer, at, model, attachments: first && attachments.length ? attachments : undefined });
    }
  }

  const title = /<title>([^<]*)<\/title>/i.exec(html)?.[1]?.trim();
  return {
    provider: "mistral",
    source: "link",
    id,
    title: title && !/^(Vibe|Le Chat|Mistral)/i.test(title) ? title.replace(/&amp;/g, "&") : messages.find((x) => x.role === "user")?.text.split(/\r?\n/)[0].slice(0, 80) || "Mistral chat",
    url,
    createdAt: messages[0]?.at,
    model: messages.find((x) => x.model)?.model,
    messages,
  };
}

/* ───────────────────────────── Saved share pages ───────────────────────────── */

/** ChatGPT's share page: the conversation is in the React Router stream (`streamController.enqueue`). */
export function chatgptFromHtml(html: string, source: ShareSource, id?: string, url?: string): ShareConversation | null {
  const re = /streamController\.enqueue\(("(?:[^"\\]|\\.)*")\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    let payload: string;
    try {
      payload = JSON.parse(m[1]);
    } catch {
      continue;
    }
    if (!payload.startsWith("[")) continue;
    let arr: unknown;
    try {
      arr = JSON.parse(payload);
    } catch {
      continue;
    }
    if (!Array.isArray(arr)) continue;
    const conv = findChatgptConversation(decodeTurboStream(arr));
    if (conv) return chatgptConversation(conv, source, id, url);
  }
  return null;
}

/**
 * A share page the visitor saved from their own browser (Ctrl+S). Their browser already opened the
 * page, so there is no server fetch and no browser check involved: we only read the file they give us.
 * Works for ChatGPT and Mistral, whose pages carry the whole conversation inline.
 */
export function conversationFromSavedPage(html: string): ShareConversation | null {
  const canonical =
    /<link[^>]+rel="canonical"[^>]+href="([^"]+)"/i.exec(html)?.[1] ||
    /<meta[^>]+property="og:url"[^>]+content="([^"]+)"/i.exec(html)?.[1] ||
    "";
  const chatgptId = /chatgpt\.com\/share\/(?:e\/)?([0-9a-f-]{36})/i.exec(canonical || html)?.[1];
  if (html.includes("streamController")) {
    const conv = chatgptFromHtml(html, "link", chatgptId, chatgptId ? `https://chatgpt.com/share/${chatgptId}` : undefined);
    if (conv) return conv;
  }
  if (html.includes("__next_f")) {
    const mistralId =
      /chat\.mistral\.ai\/work\/([0-9a-f-]{36})/i.exec(canonical)?.[1] || /"chatId":"([0-9a-f-]{36})"/.exec(mistralFlightPayload(html))?.[1];
    if (mistralId) {
      const conv = mistralConversation(html, mistralId, `https://chat.mistral.ai/work/${mistralId}`);
      if (conv?.messages.length) return conv;
    }
  }
  return null;
}

/* ───────────────────────────── Perplexity ───────────────────────────── */

/**
 * GET perplexity.ai/rest/thread/<slug>?...&with_schematized_response=true (read in the visitor's own
 * browser by the askFinz button; the endpoint answers a server with a Cloudflare check).
 * Each entry is one question (`query_str`) with typed blocks: `ask_text` holds the answer as
 * markdown, `pro_search_steps` / `plan` hold the working, `web_results` the pages it read.
 */
export function perplexityConversation(d: Any, slug: string, url?: string): ShareConversation {
  const entries: Any[] = Array.isArray(d?.entries) ? d.entries : [];
  const messages: ShareMessage[] = [];
  for (const e of entries) {
    const at = iso(e?.entry_created_datetime);
    const model = str(e?.display_model) || str(e?.user_selected_model) || undefined;
    if (str(e?.query_str)) messages.push({ role: "user", kind: "text", text: e.query_str, at });
    const blocks: Any[] = Array.isArray(e?.blocks) ? e.blocks : [];
    for (const b of blocks) {
      if (b?.plan_block && str(b.intended_usage) === "pro_search_steps") {
        const steps = (Array.isArray(b.plan_block.goals) ? b.plan_block.goals : [])
          .map((g: Any) => str(g?.description))
          .filter(Boolean);
        if (steps.length) messages.push({ role: "assistant", kind: "thinking", text: steps.join("\n"), at, model });
      } else if (b?.web_result_block) {
        const results: Any[] = Array.isArray(b.web_result_block.web_results) ? b.web_result_block.web_results : [];
        const text = results.map((r) => [str(r?.name), str(r?.url)].filter(Boolean).join(" ")).join("\n");
        if (text) messages.push({ role: "tool", kind: "tool_result", name: "web search", text: clip(text), at });
      }
    }
    // The answer last, after the working that produced it.
    const ans = blocks.find((b) => b?.markdown_block && str(b.intended_usage) === "ask_text") || blocks.find((b) => b?.markdown_block);
    const answer = str(ans?.markdown_block?.answer) || (Array.isArray(ans?.markdown_block?.chunks) ? ans.markdown_block.chunks.join("") : "");
    if (answer) messages.push({ role: "assistant", kind: "text", text: answer, at, model });
  }
  return {
    provider: "perplexity",
    source: "link",
    id: slug,
    title: str(d?.thread_metadata?.title) || str(entries[0]?.thread_title) || str(entries[0]?.query_str).slice(0, 80) || "Perplexity thread",
    url,
    createdAt: messages[0]?.at,
    model: messages.find((m) => m.model)?.model,
    messages,
  };
}

/* ───────────────────────────── Z.ai ───────────────────────────── */

/**
 * Z.ai (Open WebUI based). Read in the visitor's own browser by the askFinz button, with the session
 * Z.ai already gave that tab (the token never leaves chat.z.ai):
 *   GET  /api/v1/chats/share/<id>            -> chat.history {messages: {id: {parentId,...}}, currentId}
 *   POST /api/v1/chats/<id>/messages/batch   {ids} -> {data: {id: {role, content, content_blocks, model, usage, ...}}}
 * The conversation is the branch from `currentId` back to the root.
 */
export function zaiConversation(share: Any, batch: Any, id: string, url?: string): ShareConversation {
  const tree: Record<string, Any> = share?.chat?.history?.messages || {};
  const bodies: Record<string, Any> = batch?.data || {};
  const chain: Any[] = [];
  const seen = new Set<string>();
  let cur: string | undefined = str(share?.chat?.history?.currentId) || undefined;
  while (cur && !seen.has(cur) && (tree[cur] || bodies[cur])) {
    seen.add(cur);
    chain.push({ ...(tree[cur] || {}), ...(bodies[cur] || {}) });
    cur = str(tree[cur]?.parentId || bodies[cur]?.parentId || bodies[cur]?.parent_id) || undefined;
  }
  chain.reverse();

  const messages: ShareMessage[] = [];
  let input = 0;
  let output = 0;
  for (const m of chain) {
    const role = m?.role === "user" ? "user" : "assistant";
    const at = iso(m?.timestamp ?? m?.created_at);
    const model = str(m?.model_name) || str(m?.model) || undefined;
    const blocks: Any[] = Array.isArray(m?.content_blocks) ? m.content_blocks : [];
    let added = false;
    for (const b of blocks) {
      const type = str(b?.type).toLowerCase();
      const text = typeof b?.content === "string" ? b.content : str(b?.text);
      if (type === "text" || type === "markdown") {
        if (text) messages.push({ role, kind: "text", text, at, model: role === "assistant" ? model : undefined });
      } else if (type.includes("think") || type.includes("reason")) {
        if (text) messages.push({ role: "assistant", kind: "thinking", text, at, model });
      } else if (type.includes("tool") || type.includes("search") || type.includes("code")) {
        let body = text;
        if (!body) {
          try {
            body = JSON.stringify(b, null, 2);
          } catch {
            body = "";
          }
        }
        const isResult = type.includes("result") || type.includes("output");
        messages.push({ role: isResult ? "tool" : "assistant", kind: isResult ? "tool_result" : "tool_call", name: str(b?.name) || type, text: clip(body), at });
      } else if (text) {
        messages.push({ role, kind: "text", text, at });
      } else continue;
      added = true;
    }
    if (!added && typeof m?.content === "string" && m.content) {
      messages.push({ role, kind: "text", text: m.content, at, model: role === "assistant" ? model : undefined });
    }
    input += num(m?.usage?.prompt_tokens) ?? num(m?.usage?.input_tokens) ?? 0;
    output += num(m?.usage?.completion_tokens) ?? num(m?.usage?.output_tokens) ?? 0;
  }
  return {
    provider: "zai",
    source: "link",
    id,
    title: str(share?.title) || messages.find((x) => x.role === "user")?.text.split(/\r?\n/)[0].slice(0, 80) || "Z.ai chat",
    url,
    createdAt: iso(share?.created_at) || messages[0]?.at,
    updatedAt: iso(share?.updated_at),
    model: messages.find((x) => x.model)?.model || (Array.isArray(share?.chat?.models) ? str(share.chat.models[0]) : undefined) || undefined,
    usage: input || output ? { inputTokens: input || undefined, outputTokens: output || undefined } : undefined,
    messages,
  };
}

/* ───────────────────────────── Grok ───────────────────────────── */

/**
 * GET grok.com/rest/app-chat/share_links/<shareId>  ->  {conversation, responses[]}
 * Public, no session needed (checked 2026-11-05). Each response has `sender` (human | assistant),
 * `message` (markdown), `model`, `createTime`, plus web results, steps, tool responses and
 * attachments in their own lists.
 */
export function grokConversation(d: Any, id: string, url?: string): ShareConversation {
  const responses: Any[] = Array.isArray(d?.responses) ? d.responses : [];
  const messages: ShareMessage[] = [];
  for (const r of responses) {
    const role = str(r?.sender).toLowerCase() === "human" ? "user" : "assistant";
    const at = iso(r?.createTime);
    const model = str(r?.model) || undefined;

    const attachments: ShareAttachment[] = [];
    for (const f of [...(Array.isArray(r?.fileAttachmentsMetadata) ? r.fileAttachmentsMetadata : []), ...(Array.isArray(r?.fileAttachmentAssetMetadata) ? r.fileAttachmentAssetMetadata : [])]) {
      const mime = str(f?.fileMimeType) || str(f?.mimeType);
      attachments.push({ kind: mime.startsWith("image/") ? "image" : "file", name: str(f?.fileName) || str(f?.name) || "file", mime: mime || undefined });
    }
    const images = Array.isArray(r?.imageAttachments) ? r.imageAttachments.length : 0;
    for (let i = 0; i < images; i++) attachments.push({ kind: "image", name: "image" });

    if (role === "assistant") {
      const thinking = str(r?.thinkingTrace) || str(r?.reasoning);
      if (thinking) messages.push({ role: "assistant", kind: "thinking", text: thinking, at, model });
      for (const s of Array.isArray(r?.steps) ? r.steps : []) {
        const t = typeof s === "string" ? s : str(s?.text) || str(s?.summary) || str(s?.content);
        if (t) messages.push({ role: "assistant", kind: "thinking", text: t, at, model });
      }
      const web: Any[] = Array.isArray(r?.webSearchResults) ? r.webSearchResults : [];
      if (web.length) {
        const text = web.map((w) => [str(w?.title), str(w?.url)].filter(Boolean).join(" ")).join("\n");
        messages.push({ role: "tool", kind: "tool_result", name: "web search", text: clip(text), at });
      }
      for (const t of Array.isArray(r?.toolResponses) ? r.toolResponses : []) {
        let body = "";
        try {
          body = typeof t === "string" ? t : JSON.stringify(t, null, 2);
        } catch {
          body = "";
        }
        if (body) messages.push({ role: "tool", kind: "tool_result", name: str(t?.toolName) || str(t?.name) || "tool", text: clip(body), at });
      }
    }

    const text = str(r?.message);
    if (text || attachments.length) {
      messages.push({ role, kind: "text", text, at, model: role === "assistant" ? model : undefined, attachments: attachments.length ? attachments : undefined });
    }
  }
  return {
    provider: "grok",
    source: "link",
    id,
    title: str(d?.conversation?.title) || messages.find((m) => m.role === "user")?.text.split(/\r?\n/)[0].slice(0, 80) || "Grok chat",
    url,
    createdAt: iso(d?.conversation?.createTime) || messages[0]?.at,
    updatedAt: iso(d?.conversation?.modifyTime),
    model: messages.find((m) => m.model)?.model,
    messages,
  };
}

/**
 * Gemini's batchexecute answer (rt=c framing: )]}' then a length line and a JSON frame, one JSON
 * frame per line). Shared by the server fetcher and the askFinz button, which makes the same call from
 * the Gemini page itself. Lines are read rather than trusting the length, whose unit changes with non-ASCII.
 */
export function geminiFromBatch(text: string, id: string, url?: string): ShareConversation | null {
  for (const line of text.split(/\r?\n/)) {
    if (!line.startsWith("[")) continue;
    let frame: unknown;
    try {
      frame = JSON.parse(line);
    } catch {
      continue;
    }
    if (!Array.isArray(frame)) continue;
    for (const e of frame) {
      if (Array.isArray(e) && e[0] === "wrb.fr" && e[1] === "ujx1Bf") {
        if (typeof e[2] !== "string") return null;
        try {
          const data = JSON.parse(e[2]);
          const core = Array.isArray(data) ? data[0] : null;
          return Array.isArray(core) ? geminiConversation(core, id, url) : null;
        } catch {
          return null;
        }
      }
    }
  }
  return null;
}

/**
 * ChatGPT's React Router data address, /share/<id>.data (what the share page itself loads on client
 * navigation). A turbo-stream: the first line is the encoded value; later lines (if any) resolve
 * promises and are not needed. Answers servers and browsers alike (checked 2026-11-05), unlike the
 * HTML page, which gives servers a short preview.
 */
export function chatgptFromData(text: string, source: ShareSource, id?: string, url?: string): ShareConversation | null {
  for (const line of text.split(/\r?\n/)) {
    if (!line.startsWith("[")) continue;
    let arr: unknown;
    try {
      arr = JSON.parse(line);
    } catch {
      continue;
    }
    if (!Array.isArray(arr)) continue;
    const conv = findChatgptConversation(decodeTurboStream(arr));
    if (conv) return chatgptConversation(conv, source, id, url);
  }
  return null;
}
